import React, { useRef, useState } from "react";
import {
  Grid,
  Typography,
  Stack,
  IconButton,
  TextField,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { UploadFileOutlined } from "@mui/icons-material";
import { Box } from "@mui/system";
import { StyledSecondaryButton } from "../../../assets/styles/CommonStyles";
import { Formik } from "formik";
import { genderList, validExtensions } from "../../../constant/staticLists";
import { personalDetailsSchema, personalDetailsSchemaForSubadmin } from "../../../utils/forms/schema.personalDetails";
import FieldWrapper from "../../../components/InputField/TextFieldWrapper";
import strings from "../../../constant/strings";
import { capitalizeFirstLetter } from "../../../constant/staticHelper";
import { symptoms } from "../../../constant/staticLists";
import { useSelector } from "react-redux";
import {
  FileListsStyledGrid,
  PatientInputGrid,
  UploadFileGrid,
} from "../../../assets/styles/PatientInputFormStyles";
import RadioInputForm from "./RadioInputForm";
import SelectionDropdown from "../../../components/control/SelectionDropdown";
import colors from "../../../constant/colors";
import Dialogs from "../../../components/controls/Dialogs";
import CancelIcon from '@mui/icons-material/Cancel';
import axios from "axios";
import { PATIENT_UPLOAD, SUBADMIN_UPLOAD } from "../../../services/apiUrl";
import * as mainService from "../../../services/mainService"
const useStyles = makeStyles(() => ({
  customAutocomplete: {
    "& .MuiAutocomplete-inputRoot": { background: colors.white3 },
    "& .MuiAutocomplete-input": { padding: 0, height: 30 },
  },
}));


// Document slots on the patient booking step, same as the mobile app. The key
// is sent in documentTypes, parallel to documents.
const DOCUMENT_SLOTS = [
  { type: "injury", label: "Injury Report" },
  { type: "previousReport", label: "Previous Report" },
  { type: "previousPrescription", label: "Previous Prescription" },
];

const DESCRIPTION_MAX_LENGTH = 1000;

// Patient sections: every section may hold several files, up to this many and
// up to this total size (all its files together).
const SECTION_MAX_FILES = 5;
const SECTION_MAX_BYTES = 5 * 1024 * 1024;

const formatSize = (bytes) => {
  if (!bytes) return "0 KB";
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

// documentTypes/documentSizes are parallel to documents; drop them if they
// are not (e.g. data from an older booking state).
const parallelList = (list, documents, fallback) =>
  Array.isArray(list) && list.length === documents.length
    ? list
    : documents.map(() => fallback);

// Uploads return the stored path ("Patients/<id>/<file>"); show the file name.
const getFileName = (item) =>
  typeof item === "string" ? item.split("/").pop() : item?.name;

const PatientInputForm = ({ detailObject, data, navigate }) => {
  const ref = useRef();
  // Patient section uploads: the hidden multi-file input, the section it is
  // picking for, and the section being uploaded right now.
  const sectionInputRef = useRef();
  const pendingTypeRef = useRef(null);
  const [uploadingType, setUploadingType] = useState(null);
  const classes = useStyles();
  const [filesList, setFiles] = useState(
    !!data && !!data?.documents && data?.documents.length > 0
      ? data?.documents
      : []
  );
  const [show, setShow] = useState("");
  const [open, setOpen] = useState(false);
  const { userData, patientProfileData } = useSelector((state) => ({
    patientProfileData: state.user.patientProfile || {},
    userData: state.auth.userData
  }));

  let user = localStorage.getItem("userType")
  const genderLists = Object.keys(genderList).map((key) => ({
    label: capitalizeFirstLetter(key.replace(/_/g, " ").toLowerCase()),
    level: genderList[key],
  }));

  const symptomsList = symptoms.map((symptom, index) => ({
    label: symptom.name,
    level: symptom.name,
  }));
  const url = user === 'patient' ? PATIENT_UPLOAD() : SUBADMIN_UPLOAD

  function calculateAge(dateOfBirth) {
    const dob = new Date(dateOfBirth);
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const monthsDiff = now.getMonth() - dob.getMonth();
    if (monthsDiff < 0 || (monthsDiff === 0 && now.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  }
  const age = calculateAge(patientProfileData?.dob).toString();

  let initialValues = {
    name: patientProfileData.name,
    age: age,
    weight: patientProfileData.weight,
    gender: patientProfileData.gender,
    symptoms:
      !!data && !!data?.symptoms && data?.symptoms.length > 0
        ? data?.symptoms
        : [],
    documents:
      !!data && !!data?.documents && data?.documents.length > 0
        ? data?.documents
        : [],
    // Kept parallel to documents; restored when the patient comes back to this step.
    documentTypes: parallelList(data?.documentTypes, data?.documents || [], ""),
    // Bytes of each document, for the per-section size limit. Never sent to
    // the API (Checkout strips it).
    documentSizes: parallelList(data?.documentSizes, data?.documents || [], 0),
    description: data?.description || "",
  };

  let initialValuesForSubadmin = {
    patientId: patientProfileData.patientId,
    symptoms:
      !!data && !!data?.symptoms && data?.symptoms.length > 0
        ? data?.symptoms
        : [],
    documents:
      !!data && !!data?.documents && data?.documents.length > 0
        ? data?.documents
        : [],
  };

  const handlePatientDetail = (values) => {
    const data = {
      symptoms: values.symptoms,
      documents: values.documents,
      patientId: values.patientId
    };
    // Only the patient create-appointment schema accepts these; the sub-admin
    // one rejects unknown keys, so its payload stays unchanged.
    if (navigate !== "subadmin") {
      data.documentTypes = values.documentTypes || [];
      data.documentSizes = values.documentSizes || [];
      data.description = (values.description || "").trim();
    }
    detailObject(data, 4);
  };

  return (
    <>
      <Grid
        item
        xs={12}
        sx={{
          fontSize: "14px",
          lineHeight: "18px",
          color: "#2B2B2B",
          paddingBottom: "16px",
        }}
      >
        Personal Details
      </Grid>
      <PatientInputGrid item xs={12}>
        <Grid container justifyContent={"space-between"} spacing={4}>
          <Formik
            initialValues={navigate != 'subadmin' ? initialValues : initialValuesForSubadmin}
            validationSchema={navigate != 'subadmin' ? personalDetailsSchema : personalDetailsSchemaForSubadmin}
            onSubmit={(values) => handlePatientDetail(values)}
            enableReinitialize
          >
            {({ values, handleSubmit, setFieldValue, errors }) => {
              // Symptoms and documents are optional now. For a patient the
              // personal details above are read-only (filled from the profile),
              // so nothing on this step can be invalid and nothing may block the
              // button; only the subadmin types a patientId, so that is the one
              // field that still gates it.
              const isSubadmin = navigate === "subadmin";
              const isFormValid = isSubadmin ? !errors.patientId : true;
              // Same reason: a patient skips Formik validation of the read-only
              // fields, which are not part of the payload anyway, so a gap in the
              // profile cannot silently swallow the click.
              const handleProceed = isSubadmin
                ? handleSubmit
                : () => handlePatientDetail(values);

              const notify = (message) => {
                setShow(message);
                setOpen(true);
              };

              // Files already attached to one patient section.
              const sectionFiles = (type) =>
                values.documents
                  .map((item, index) => ({
                    item,
                    index,
                    size: (values.documentSizes || [])[index] || 0,
                  }))
                  .filter((file) => (values.documentTypes || [])[file.index] === type);

              const openSectionPicker = (type) => {
                if (uploadingType) return;
                if (sectionFiles(type).length >= SECTION_MAX_FILES) {
                  notify(`You can add up to ${SECTION_MAX_FILES} files in each section.`);
                  return;
                }
                pendingTypeRef.current = type;
                sectionInputRef.current.click();
              };

              // Patient: several files at once into one section. Everything is
              // checked before anything is uploaded, so a rejected pick changes nothing.
              const uploadSectionFiles = async (e) => {
                const picked = Array.from(e.target.files || []);
                e.target.value = ""; // the same files can be picked again
                const type = pendingTypeRef.current;
                pendingTypeRef.current = null;
                if (!type || picked.length === 0) return;

                const slot = DOCUMENT_SLOTS.find((item) => item.type === type);
                const existing = sectionFiles(type);

                const badType = picked.find(
                  (file) => !validExtensions.includes(file.name.split(".").pop().toLowerCase())
                );
                if (badType) {
                  notify(`"${badType.name}" is not supported. Please select pdf, png, jpeg or jpg files.`);
                  return;
                }
                if (existing.length + picked.length > SECTION_MAX_FILES) {
                  notify(`You can add up to ${SECTION_MAX_FILES} files in each section. ${slot.label} already has ${existing.length}.`);
                  return;
                }
                // Uploads are stored by file name, so a second file with the same
                // name would overwrite the first one.
                const attachedNames = values.documents.map(getFileName);
                const pickedNames = picked.map((file) => file.name);
                const duplicate = pickedNames.find(
                  (name, index) => attachedNames.includes(name) || pickedNames.indexOf(name) !== index
                );
                if (duplicate) {
                  notify(`A file named "${duplicate}" is already added. Please rename it and try again.`);
                  return;
                }
                const total =
                  existing.reduce((sum, file) => sum + file.size, 0) +
                  picked.reduce((sum, file) => sum + file.size, 0);
                if (total > SECTION_MAX_BYTES) {
                  notify(
                    `The files in ${slot.label} add up to ${formatSize(total)}, but each section allows up to 5 MB in total. ` +
                    `Please compress the files (or upload fewer) and try again.`
                  );
                  return;
                }

                setUploadingType(type);
                const uploaded = [];
                const failed = [];
                for (const file of picked) {
                  const formData = new FormData();
                  formData.append("file", file);
                  try {
                    const response = await mainService.fileUpload(url, formData, userData.token);
                    const path = response?.response?.data;
                    if (response?.response_type === "success" && typeof path === "string" && path) {
                      uploaded.push({ path, size: file.size });
                    } else {
                      failed.push({ name: file.name, reason: response?.response?.message });
                    }
                  } catch (error) {
                    console.error("Error:", error);
                    failed.push({ name: file.name });
                  }
                }
                setUploadingType(null);

                if (uploaded.length) {
                  const documents = [...values.documents, ...uploaded.map((file) => file.path)];
                  setFiles(documents);
                  setFieldValue("documents", documents);
                  setFieldValue("documentTypes", [...(values.documentTypes || []), ...uploaded.map(() => type)]);
                  setFieldValue("documentSizes", [...(values.documentSizes || []), ...uploaded.map((file) => file.size)]);
                }
                if (failed.length) {
                  const names = failed.map((file) => `"${file.name}"`).join(", ");
                  const reason = failed.find((file) => file.reason)?.reason;
                  notify(
                    `${failed.length} of ${picked.length} file(s) could not be uploaded: ${names}.` +
                    (reason ? ` ${reason}` : " Please try again.")
                  );
                }
              };

              const uploadFile = async (e) => {
                const limit = 2;
                const files = e.target.files;
                if (!files || !files[0]) return;
                const fileExtension = files[0].name.split('.').pop().toLowerCase();
                if (!validExtensions.includes(fileExtension)) {
                  setOpen(true);
                  setShow("Please select file format from pdf,png,jpeg,jpg")
                }
                else if (e.target.files[0].size / 1024 / 1024 > limit) {
                  setOpen(true);
                  setShow("The size of file cannot exceed 2 MB. Please try with a smaller file.")
                }
                else {
                  const formData = new FormData();
                  formData.append('file', files[0]);

                  try {

                    const response = await mainService.fileUpload(url, formData, userData.token)
                    const temp = [...filesList, response.response.data]
                    setFiles(temp);
                    setFieldValue("documents", temp);
                  } catch (error) {
                    console.error('Error:', error);
                  }

                }
                // Let the same file be picked again after a remove or an error.
                e.target.value = "";
              };

              const handleFileLength = (values, fieldName) => {
                if (filesList.length < 5)
                  ref.current.click();
                else {
                  setOpen(true)
                  setShow("Cannot upload more than 5 files")
                }
              };

              const handleDropdownValue = (data, fieldName, values) => {
                if (data) {
                  if (fieldName == "gender") {
                    const ids = data.level;
                    setFieldValue("gender", ids);
                  }
                  if (fieldName == "symptoms") {
                    let arr = [];
                    data.map((item) => arr.push(item.level));
                    setFieldValue("symptoms", arr);

                  }
                } else {
                  setFieldValue("gender", "");
                  setFieldValue("symptoms", []);
                }
              }

              const removeFile = (id) => {
                if (uploadingType) return;
                let temp = values.documents.filter((item, index) => index != id)
                setFiles(temp);
                setFieldValue("documents", temp);
                if (!isSubadmin) {
                  setFieldValue("documentTypes", (values.documentTypes || []).filter((item, index) => index !== id));
                  setFieldValue("documentSizes", (values.documentSizes || []).filter((item, index) => index !== id));
                }
              }

              return (
                <>
                  {navigate === 'subadmin' ? <Grid item xs={12} md={3}>
                    <FieldWrapper
                      type="text"
                      name="patientId"
                      label={`${strings.patientId}`}
                    />
                  </Grid> :
                    <>
                      <Grid item xs={12} md={3}>
                        <FieldWrapper
                          type="text"
                          name="name"
                          disabled={true}
                          label={`${strings.patientFullName}`}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FieldWrapper
                          type="text"
                          name="age"
                          disabled={true}
                          label={`${strings.age}`}
                        />
                      </Grid>
                      <Grid item xs={12} md={3} position={"relative"}>
                        <FieldWrapper
                          type="text"
                          name="weight"
                          label={`${strings.weight}`}
                          disabled={true}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <SelectionDropdown
                          height={"48px"}
                          className={classes.customAutocomplete}
                          name="gender"
                          disabled={true}
                          value={values.gender}
                          label={`${strings.selectGender}`}
                          list={genderLists}
                          handleDropdownValue={(data) =>
                            handleDropdownValue(data, "gender")
                          }
                        />
                      </Grid>
                    </>}
                  <Grid
                    item
                    xs={12}
                    sx={{
                      fontSize: "14px",
                      color: "#2B2B2B",
                    }}
                  >
                    Symptoms
                  </Grid>
                  <Grid item xs={12} mt={-4}>
                    <Stack spacing={3} sx={{ width: 500 }}>
                      <SelectionDropdown
                        height={"48px"}
                        navigate={"multiple"}
                        className={classes.customAutocomplete}
                        name="symptoms"
                        value={values.symptoms}
                        placeholdere={strings.searchByDoctorName}
                        list={symptomsList}
                        handleDropdownValue={(data) =>
                          handleDropdownValue(data, "symptoms", values.symptoms)
                        }
                      />
                    </Stack>
                  </Grid>
                  {isSubadmin ? (
                    <>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        fontSize: "14px",
                        lineHeight: "18px",
                        color: "var(--black, #2B2B2B)",
                        fontFamily: "Poppins",
                        fontStyle: "normal",
                        fontWeight: "500",
                      }}
                    >
                      Upload Documents (optional)
                      <Box
                        sx={{
                          fontSize: "13px",
                          lineHeight: "20px",
                          fontWeight: "400",
                          color: "#6B6B6B",
                          paddingTop: "4px",
                        }}
                      >
                        Anything that helps the doctor understand your condition is
                        welcome here, such as an earlier prescription, a lab or test
                        report, or a photo of the affected area. You can also skip
                        this and continue. Up to 5 files, each under 10 MB.
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container>
                        <UploadFileGrid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          onClick={handleFileLength}
                        >
                          <Box>
                            <Box>
                              <UploadFileOutlined />
                            </Box>
                            <Typography className="filetypes">
                              pdf, jpg, jpeg & png files
                            </Typography>
                            <input
                              type="file"
                              ref={ref}
                              name="documents"
                              onChange={(data) => uploadFile(data, "documents")}
                            />
                          </Box>
                        </UploadFileGrid>
                        <FileListsStyledGrid
                          item
                          xs={12}
                          sm={6}
                          md={9}
                          paddingLeft={"16px"}
                        >
                          {values.documents.map((item, index) => (

                            <Box paddingBottom={"16px"}>
                              <UploadFileOutlined /> {getFileName(item)} <IconButton onClick={() => removeFile(index)} ><CancelIcon /></IconButton>
                            </Box>
                          ))}
                        </FileListsStyledGrid>
                      </Grid>
                    </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12} mt={-2}>
                        <Box
                          sx={{
                            fontSize: "14px",
                            lineHeight: "18px",
                            color: "#2B2B2B",
                            paddingBottom: "8px",
                          }}
                        >
                          What's happening? (optional)
                        </Box>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          name="description"
                          value={values.description}
                          onChange={(e) => setFieldValue("description", e.target.value)}
                          placeholder="Describe your problem, when it started and anything else the doctor should know"
                          inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
                          helperText={`${(values.description || "").length}/${DESCRIPTION_MAX_LENGTH}`}
                          FormHelperTextProps={{ sx: { textAlign: "right", mx: 0 } }}
                          sx={{ "& .MuiInputBase-root": { background: colors.white3 } }}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sx={{
                          fontSize: "14px",
                          lineHeight: "18px",
                          color: "var(--black, #2B2B2B)",
                          fontFamily: "Poppins",
                          fontStyle: "normal",
                          fontWeight: "500",
                        }}
                      >
                        Upload Documents (optional)
                        <Box
                          sx={{
                            fontSize: "13px",
                            lineHeight: "20px",
                            fontWeight: "400",
                            color: "#6B6B6B",
                            paddingTop: "4px",
                          }}
                        >
                          Add anything that helps the doctor understand your
                          condition, or skip this and continue. You can add
                          several pdf, jpg, jpeg or png files to each section, up
                          to 5 MB in total per section.
                        </Box>
                        <input
                          type="file"
                          multiple
                          ref={sectionInputRef}
                          name="documents"
                          accept=".pdf,.png,.jpg,.jpeg"
                          style={{ display: "none" }}
                          onChange={uploadSectionFiles}
                        />
                      </Grid>
                      {DOCUMENT_SLOTS.map((slot) => {
                        const files = sectionFiles(slot.type);
                        const used = files.reduce((sum, file) => sum + file.size, 0);
                        const isUploading = uploadingType === slot.type;
                        return (
                        <Grid item xs={12} md={4} key={slot.type}>
                          <Box
                            sx={{
                              fontSize: "14px",
                              lineHeight: "18px",
                              color: "#2B2B2B",
                              paddingBottom: "8px",
                            }}
                          >
                            {slot.label}
                          </Box>
                          <UploadFileGrid
                            onClick={() => openSectionPicker(slot.type)}
                            sx={{
                              height: 140,
                              "& > .MuiBox-root": { height: 140 },
                              cursor: uploadingType ? "default" : "pointer",
                              opacity: uploadingType && !isUploading ? 0.6 : 1,
                            }}
                          >
                            <Box>
                              <Box>
                                {isUploading ? <CircularProgress size={22} /> : <UploadFileOutlined />}
                              </Box>
                              <Typography className="filetypes">
                                {isUploading ? "Uploading..." : "pdf, jpg, jpeg & png files"}
                              </Typography>
                            </Box>
                          </UploadFileGrid>
                          <Box
                            sx={{
                              fontSize: "12px",
                              color: used > SECTION_MAX_BYTES ? "#D32F2F" : "#6B6B6B",
                              paddingTop: "6px",
                            }}
                          >
                            {files.length} file(s) · {formatSize(used)} of 5 MB used
                          </Box>
                          <FileListsStyledGrid paddingTop={"8px"}>
                            {files.map(({ item, index, size }) => (
                              <Box key={`${slot.type}-${index}`} display={"flex"} alignItems={"center"} sx={{ wordBreak: "break-all" }}>
                                <UploadFileOutlined /> {getFileName(item)}
                                {size ? ` (${formatSize(size)})` : ""}
                                <IconButton onClick={() => removeFile(index)} disabled={!!uploadingType}><CancelIcon /></IconButton>
                              </Box>
                            ))}
                          </FileListsStyledGrid>
                        </Grid>
                        );
                      })}
                    </>
                  )}
                  <Grid item xs={12} display={"flex"} justifyContent={"center"}>
                    <StyledSecondaryButton
                      onClick={handleProceed}
                      disabled={!isFormValid || !!uploadingType}
                      sx={{ width: "482px", textTransform: "capitalize" }}
                    >
                      Proceed to book appointment
                    </StyledSecondaryButton>
                  </Grid>
                </>
              );
            }}
          </Formik>
        </Grid>
        {open && <Dialogs setOpen={setOpen} open={open} content={show} />}
      </PatientInputGrid>

    </>
  );
};

export default React.memo(PatientInputForm);
